import { z } from 'zod';
import { RULE_IDS, type RuleId } from './rules.js';
import { validateAgreementText } from './format.js';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha AAAA-MM-DD');
const week = z.string().regex(/^\d{4}-W\d{2}$/, 'Semana ISO AAAA-Www');
const light = z.enum(['rojo', 'ambar', 'verde']);

export const createAgreementSchema = z.object({
  week,
  text: z
    .string()
    .min(1)
    .max(500)
    .refine((t) => validateAgreementText(t) === null, {
      message: 'Empieza por un verbo en infinitivo',
    }),
  responsibleId: z.string().uuid(),
  responsibleName: z.string().min(1).max(200),
  dueDate: isoDate,
  caseKey: z.string().max(200).nullable().optional(),
  caseLabel: z.string().max(300).nullable().optional(),
});
export type CreateAgreementInput = z.infer<typeof createAgreementSchema>;

export const updateAgreementSchema = z
  .object({
    status: light.nullable().optional(),
    comment: z.string().max(1000).nullable().optional(),
    dueDate: isoDate.optional(),
    text: z
      .string()
      .min(1)
      .max(500)
      .refine((t) => validateAgreementText(t) === null, { message: 'Empieza por un verbo en infinitivo' })
      .optional(),
    responsibleId: z.string().uuid().optional(),
    responsibleName: z.string().min(1).max(200).optional(),
  })
  // El responsable se cambia entero (id + nombre), nunca a medias
  .refine((v) => (v.responsibleId === undefined) === (v.responsibleName === undefined), {
    message: 'Responsable incompleto',
    path: ['responsibleId'],
  });
export type UpdateAgreementInput = z.infer<typeof updateAgreementSchema>;

export const tandemSheetSchema = z.object({
  week,
  affId: z.string().uuid(),
  caseKey: z.string().min(1).max(200),
  quePasa: z.string().max(1000).default(''),
  queHemosIntentado: z.string().max(1000).default(''),
  porQueAtascado: z.string().max(1000).default(''),
  queProponemos: z.string().max(1000).default(''),
});
export type TandemSheetInput = z.infer<typeof tandemSheetSchema>;

export const officeSettingsSchema = z.object({
  ideaFuerza: z.string().max(300).nullable(),
});

export const meetingSensitiveSchema = z.object({ sensitive: z.boolean() });

export const ruleParamsSchema = z.array(
  z.object({
    ruleId: z.enum(RULE_IDS as [RuleId, ...RuleId[]]),
    amber: z.number(),
    red: z.number(),
    enabled: z.boolean(),
    /** Umbrales cuando falta la referencia principal (S1: antigüedad si no hay vencimiento ni SLO) */
    fallback: z.object({ amber: z.number(), red: z.number() }).optional(),
  }),
);

const pts = z.number().finite().min(-1000).max(1000);
const ruleBonus = z.object(Object.fromEntries(RULE_IDS.map((id) => [id, pts])) as Record<RuleId, typeof pts>);
export const scoringConfigSchema = z.object({
  lightPoints: z.object({ rojo: pts, ambar: pts }),
  segmentPoints: z.object({ A: pts, B: pts, C: pts, D: pts, E: pts, none: pts }),
  meetingSoon: z.object({ days: z.number().int().min(0).max(365), points: pts }),
  ratio: z.object({ weight: pts, cap: z.number().min(0).max(100) }),
  ruleBonus,
  table: z.object({
    max: z.number().int().min(1).max(50),
    allocation: z.enum(['global', 'quota', 'minimum']),
    minPerBlock: z.number().int().min(0).max(12),
    ordering: z.enum(['tiered', 'score']),
    groupByCase: z.boolean(),
  }),
  blockMinutes: z.object({
    J: z.number().min(0).max(240),
    S: z.number().min(0).max(240),
    C: z.number().min(0).max(240),
    F: z.number().min(0).max(240),
  }),
});
export const saveScoringSchema = z.object({
  config: scoringConfigSchema,
  note: z.string().max(300).optional(),
});

export const displayConfigSchema = z.object({
  hiddenKpis: z.array(z.enum(['K1', 'K2', 'K3', 'K4', 'K5', 'K6'])).max(6),
});

const fourLines = z.object({
  quePasa: z.string().max(1000).default(''),
  queHemosIntentado: z.string().max(1000).default(''),
  porQueAtascado: z.string().max(1000).default(''),
  queProponemos: z.string().max(1000).default(''),
});

/** Alta de un caso manual (lo trae el tándem y no está en la mesa ni en la capa de detalle) */
export const createManualCaseSchema = z.object({
  communityId: z.string().uuid(),
  title: z.string().trim().min(3).max(200),
  block: z.enum(['J', 'S', 'C', 'F']),
  light: z.enum(['rojo', 'ambar']),
  affId: z.string().uuid().nullable().optional(),
  erpUrl: z.string().url().max(500).nullable().optional(),
  note: z.string().max(1000).nullable().optional(),
  /** Si se rellena, se añade a la hoja del tándem de esta semana */
  sheet: fourLines
    .extend({ week: z.string().regex(/^\d{4}-W\d{2}$/) })
    .nullable()
    .optional(),
});
export type CreateManualCaseInput = z.infer<typeof createManualCaseSchema>;

export const updateManualCaseSchema = z.object({
  light: z.enum(['rojo', 'ambar']).optional(),
  title: z.string().trim().min(3).max(200).optional(),
  block: z.enum(['J', 'S', 'C', 'F']).optional(),
  closed: z.boolean().optional(),
});
export type UpdateManualCaseInput = z.infer<typeof updateManualCaseSchema>;

/** Roles ERP de mando que dan acceso al panel (por defecto). Se editan en el admin (app_settings.access). */
export const DEFAULT_ACCESS_ROLES = [
  'Director oficina',
  'Director Regional',
  'Director Territorial',
  'Jefe de equipo Negocio',
];

export const accessConfigSchema = z.object({ roles: z.array(z.string().min(1).max(200)).max(100) });
export type AccessConfig = z.infer<typeof accessConfigSchema>;

export const accessCheckSchema = z.object({ email: z.string().email().max(200) });
